import {
  INVOICE_PAYMENT_STATUSES,
  INVOICE_STATUSES,
  type InvoicePaymentStatus,
  type InvoiceStatus,
} from '@monorepo/shared/schemas/common'
import type { Invoice } from '@monorepo/shared/schemas/invoices'
import { useEffect, useState } from 'react'
import { invoicesService } from '@/features/invoices'
import { toast } from '@/lib/toast'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

const formatLabel = (value: string) =>
  value
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

interface UpdateInvoiceStatusDialogProps {
  invoice: Invoice | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function UpdateInvoiceStatusDialog({
  invoice,
  open,
  onOpenChange,
  onSuccess,
}: UpdateInvoiceStatusDialogProps) {
  const [invoiceStatus, setInvoiceStatus] = useState<InvoiceStatus | ''>('')
  const [paymentStatus, setPaymentStatus] = useState<InvoicePaymentStatus | ''>(
    ''
  )
  const [loading, setLoading] = useState(false)

  // Initialize status when invoice changes
  useEffect(() => {
    if (invoice) {
      setInvoiceStatus(invoice.status)
      setPaymentStatus(invoice.payment_status)
    }
  }, [invoice])

  const handleSubmit = async () => {
    if (!invoice || !invoiceStatus) {
      return
    }

    setLoading(true)
    try {
      if (invoice.status === 'draft') {
        await invoicesService.updateInvoice(invoice.id, {})
        toast.success('Invoice status updated')
      } else {
        toast.info(
          'For non-draft invoices, use Finalize or Void actions instead'
        )
      }
      onOpenChange(false)
      onSuccess()
    } catch {
      toast.error('Failed to update status')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setInvoiceStatus('')
    setPaymentStatus('')
    onOpenChange(false)
  }

  const isDraft = invoice?.status === 'draft'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Update Status</DialogTitle>
          <DialogDescription>
            {invoice && (
              <div>
                {invoice.customer?.name && (
                  <>
                    Update status for <strong>{invoice.customer.name}</strong>
                    <br />
                  </>
                )}
                Invoice: {invoice.invoice_number}
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          {!isDraft && (
            <div className='p-3 bg-muted/50 rounded-md text-sm text-muted-foreground'>
              Direct status changes are only available for draft invoices. Use
              Finalize, Record Payment, or Void actions from the invoice menu
              for other status transitions.
            </div>
          )}

          <div className='space-y-2'>
            <Label htmlFor='invoice-status'>Invoice Status</Label>
            <Select
              value={invoiceStatus}
              onValueChange={value => setInvoiceStatus(value as InvoiceStatus)}
              disabled={!isDraft}
            >
              <SelectTrigger>
                <SelectValue placeholder='Select invoice status' />
              </SelectTrigger>
              <SelectContent>
                {INVOICE_STATUSES.map(status => (
                  <SelectItem key={status} value={status}>
                    {formatLabel(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='payment-status'>Payment Status</Label>
            <Select
              value={paymentStatus}
              onValueChange={value =>
                setPaymentStatus(value as InvoicePaymentStatus)
              }
              disabled
            >
              <SelectTrigger>
                <SelectValue placeholder='Select payment status' />
              </SelectTrigger>
              <SelectContent>
                {INVOICE_PAYMENT_STATUSES.map(status => (
                  <SelectItem key={status} value={status}>
                    {formatLabel(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='text-xs text-muted-foreground'>
              Payment status is managed through Record Payment actions
            </p>
          </div>

          <div className='flex justify-end gap-3 pt-4'>
            <Button variant='outline' onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!invoiceStatus || loading || !isDraft}
            >
              {loading ? 'Updating...' : 'Update Status'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
